<template>
  <div class="soha-image-cropper">
    <!-- Modal -->
    <soha-modal
      :id="modalId"
      :title="title"
      hide-footer
      @hide="$emit('cancel')"
    >
      <template #body>
        <div class="cropper-wrapper">
          <div class="cropper-container">
            <cropper
              ref="cropper"
              :src="imageSrc"
              :stencil-props="stencilProps"
              :background-class="'transparent-background'"
              image-restriction="none"
              :min-width="minWidth"
              :min-height="minHeight"
            />
          </div>
          <div class="controls text-center mt-3">
            <small class="text-muted">
              تصویر را جابجا کرده و اندازه‌اش را تنظیم کنید
            </small>
          </div>
          
          <!-- Controls -->
          <div class="controls mt-3">
            <soha-button-icon
              icon="plus"
              @btn-click="zoom(-0.0005)"
            />
            <soha-button-icon
              icon="minus"
              @btn-click="zoom(0.05)"
            />
            <soha-button-icon
              icon="refresh"
              @btn-click="reset"
            />
          </div>
        </div>
        
        <!-- Action Buttons -->
        <div class="action-buttons mt-3">
          <soha-button
            class="w-100"
            text="برش تصویر"
            :loading="loading"
            @btn-click="cropImage"
          />
        </div>
      </template>
    </soha-modal>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: "SohaImageCropper",
  
  components: {
    Cropper,
  },
  
  props: {
    // ورودی‌ها
    imageSrc: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'برش تصویر'
    },
    aspectRatio: {
      type: Number,
      default: 1
    },
    minWidth: {
      type: Number,
      default: 50
    },
    minHeight: {
      type: Number,
      default: 50
    },
    outputFormat: {
      type: String,
      default: 'png',
      validator: (value) => ['png', 'jpeg', 'jpg'].includes(value)
    },
    modalId: {
      type: String,
      default: 'image-cropper-modal'
    }
  },
  
  emits: ['cropped', 'cancel', 'error'],
  
  data() {
    return {
      loading: false,
      stencilProps: {
        aspectRatio: this.aspectRatio,
        movable: true,
        resizable: true,
        handlers: {
          eastNorth: true,
          westNorth: true,
          eastSouth: true,
          westSouth: true
        },
        lines: {
          east: true,
          west: true,
          north: true,
          south: true
        }
      }
    };
  },
  
  methods: {
    show() {
      this.$modal.show(this.modalId);
    },
    
    hide() {
      this.$modal.hide(this.modalId);
    },
    
    zoom(ratio) {
      if (this.$refs.cropper) {
        this.$refs.cropper.zoom(ratio);
      }
    },
    
    reset() {
      if (this.$refs.cropper) {
        this.$refs.cropper.reset();
      }
    },
    
    async cropImage() {      
      this.loading = true;
      if (!this.$refs.cropper) {
        this.$emit('error', 'ابزار برش در دسترس نیست');
        return;
      }
      
      try {
        const { canvas } = this.$refs.cropper.getResult();
        
        if (!canvas) {
          throw new Error('خطا در ایجاد تصویر برش خورده');
        }
        
        const mimeType = this.outputFormat === 'png' ? 'image/png' : 'image/jpeg';
        const fileExtension = this.outputFormat === 'png' ? 'png' : 'jpg';
        
        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, mimeType, 1.0);
        });
        
        const file = new File([blob], `cropped-image.${fileExtension}`, {
          type: mimeType,
        });
        
        // خروجی
        this.$emit('cropped', {
          file,
          blob,
          dataUrl: URL.createObjectURL(blob),
          width: canvas.width,
          height: canvas.height
        });
        
        this.hide();
        
      } catch (error) {
        this.$emit('error', error.message);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.cropper-wrapper {
  padding: 10px;
}

.cropper-container {
  height: 350px;
  width: 350px;
  margin: auto;
  background: 
    repeating-conic-gradient(#f8f8f8 0% 25%, #e8e8e8 0% 50%) 
    50% / 20px 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.controls {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* استایل‌های transparency */
:deep(.transparent-background) {
  background: transparent !important;
}

:deep(.vue-advanced-cropper__background) {
  background: transparent !important;
}

:deep(.vue-advanced-cropper__image) {
  background: transparent !important;
}

:deep(.vue-advanced-cropper__stencil) {
  border: 2px dashed #007bff;
}
</style>